import type { SuperAdminShopSettingsDto } from "@babackapp/private-api-client/types";
import { ChevronRight, ExternalLink } from "lucide-react";
import { ConfigurationSection } from "./shop-account-sections";

/** Groups of the merchant's settings, and where Returns edits them. */
const SETTINGS_GROUPS: Array<{
  key: keyof SuperAdminShopSettingsDto;
  title: string;
  returnsPath: string;
}> = [
  {
    key: "preferences",
    title: "Préférences",
    returnsPath: "settings/shop-informations",
  },
  {
    key: "exchange",
    title: "Échanges",
    returnsPath: "settings/customer-portal",
  },
  {
    key: "discount",
    title: "Avoirs",
    returnsPath: "settings/customer-portal",
  },
  { key: "giftCode", title: "Bons cadeaux", returnsPath: "gift-codes" },
  {
    key: "refund",
    title: "Remboursement",
    returnsPath: "settings/customer-portal",
  },
  {
    key: "returnToStore",
    title: "Retour en boutique",
    returnsPath: "settings/customer-portal",
  },
  {
    key: "workflowLogistician",
    title: "Workflow logisticien",
    returnsPath: "settings/shop-informations",
  },
  {
    key: "trackingPreferences",
    title: "Tracking",
    returnsPath: "settings/shop-informations",
  },
  {
    key: "storeShippingSettings",
    title: "Expédition en boutique",
    returnsPath: "settings/shop-informations",
  },
  { key: "admin", title: "Admin Baback", returnsPath: "" },
];

const LONG_TEXT = 160;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function Scalar({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span
        className={[
          "rounded-full px-1.5 py-0.5 text-[11px] font-medium",
          value
            ? "bg-green-100 text-green-700"
            : "bg-muted text-muted-foreground",
        ].join(" ")}
      >
        {value ? "Oui" : "Non"}
      </span>
    );
  }
  const text = String(value);
  return (
    <span className="break-all font-mono text-xs" title={text}>
      {text.length > LONG_TEXT ? `${text.slice(0, LONG_TEXT)}…` : text}
    </span>
  );
}

function isScalarList(value: unknown[]): boolean {
  return value.every((item) => !isPlainObject(item) && !Array.isArray(item));
}

/** Read only: a viewer, never an editor (rule of the admin, BAB-3310). */
function SettingsTree({ value }: { value: Record<string, unknown> }) {
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground">Vide</p>;
  }

  return (
    <dl className="divide-y">
      {entries.map(([key, entry]) => {
        const isNested =
          isPlainObject(entry) ||
          (Array.isArray(entry) && entry.length > 0 && !isScalarList(entry));

        if (!isNested) {
          return (
            <div key={key} className="flex gap-4 py-1.5">
              <dt className="w-72 shrink-0 font-mono text-xs text-muted-foreground">
                {key}
              </dt>
              <dd className="min-w-0 text-sm">
                {Array.isArray(entry) ? (
                  entry.length === 0 ? (
                    <span className="text-muted-foreground">[]</span>
                  ) : (
                    <Scalar value={entry.join(", ")} />
                  )
                ) : (
                  <Scalar value={entry} />
                )}
              </dd>
            </div>
          );
        }

        const children = Array.isArray(entry)
          ? Object.fromEntries(entry.map((item, index) => [index, item]))
          : (entry as Record<string, unknown>);
        return (
          <details key={key} className="group py-1.5">
            <summary className="flex cursor-pointer list-none items-center gap-1 font-mono text-xs text-muted-foreground">
              <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
              {key}
              {Array.isArray(entry) && (
                <span className="ml-1">({entry.length})</span>
              )}
            </summary>
            <div className="ml-4 mt-1 border-l pl-3">
              <SettingsTree value={children} />
            </div>
          </details>
        );
      })}
    </dl>
  );
}

/**
 * The merchant's own settings, read only (BBS-406): Returns edits them,
 * through the services that validate them. Secrets come masked.
 */
export function ShopSettingsSection({
  shopId,
  settings,
}: {
  shopId: string;
  settings: SuperAdminShopSettingsDto;
}) {
  return (
    <ConfigurationSection
      title="Réglages du marchand (lecture seule)"
      description="Ils se modifient dans Returns. Les jetons et clés sont masqués."
    >
      <div className="divide-y">
        {SETTINGS_GROUPS.map(({ key, title, returnsPath }) => (
          <details key={key} className="group px-5 py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                {title}
              </span>
              {returnsPath && (
                <a
                  href={`/shops/${shopId}/${returnsPath}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-violet-700 hover:underline"
                >
                  Modifier dans Returns
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </summary>
            <div className="mt-3">
              <SettingsTree value={settings[key]} />
            </div>
          </details>
        ))}
      </div>
    </ConfigurationSection>
  );
}
